"use client";

import Link from "next/link";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { FileText, MessageCircleQuestion, ClipboardCheck, ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/language-context";

export default function Home() {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      {/* Hero Section */}
      <main className="flex-1">
        <section className="relative py-40 px-6 text-center overflow-hidden min-h-[700px] flex items-center justify-center">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0 scale-105 animate-slow-zoom"
            style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2000&auto=format&fit=crop")' }}
          ></div>
          {/* Overlay - Blue Tint */}
          <div className="absolute inset-0 bg-blue-900/80 dark:bg-black/80 z-0"></div>

          <div className="container mx-auto max-w-4xl relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white px-4 py-1.5 rounded-full text-xs font-semibold mb-8 shadow-sm backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
              {t.hero.badge}
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-tight mb-8 drop-shadow-lg">
              {t.hero.title_line1}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-200">
                {t.hero.title_line2}
              </span>
            </h1>
            <p className="text-xl text-gray-200 mb-12 max-w-2xl mx-auto leading-relaxed font-light" dangerouslySetInnerHTML={{ __html: t.hero.desc }}>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                href="/upload"
                className="group flex items-center justify-center gap-2 bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 rounded-xl text-lg font-bold shadow-xl transition-all hover:scale-105"
              >
                {t.hero.cta_primary}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <button className="flex items-center justify-center gap-2 text-white border border-white/30 hover:bg-white/10 px-8 py-4 text-lg font-medium transition rounded-xl backdrop-blur-sm">
                <PlayCircle className="w-5 h-5" />
                {t.hero.cta_secondary}
              </button>
            </div>
          </div>
        </section>

        {/* Features Grid - Bento Style */}
        <section className="py-24 px-6 bg-gray-50 dark:bg-black/50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{t.features.title}</h2>
              <p className="text-gray-500 dark:text-gray-400">{t.features.subtitle}</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="group p-8 rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:shadow-xl hover:border-blue-500/30 dark:hover:border-blue-500/30 transition-all duration-300">
                <div className="w-14 h-14 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 duration-300">
                  <FileText className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{t.features.f1_title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t.features.f1_desc}
                </p>
              </div>

              {/* Feature 2 */}
              <div className="group p-8 rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:shadow-xl hover:border-purple-500/30 dark:hover:border-purple-500/30 transition-all duration-300">
                <div className="w-14 h-14 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 duration-300">
                  <MessageCircleQuestion className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{t.features.f2_title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t.features.f2_desc}
                </p>
              </div>

              {/* Feature 3 */}
              <div className="group p-8 rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:shadow-xl hover:border-green-500/30 dark:hover:border-green-500/30 transition-all duration-300">
                <div className="w-14 h-14 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 duration-300">
                  <ClipboardCheck className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{t.features.f3_title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t.features.f3_desc}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
